import { useEffect, useState, useRef, useMemo } from 'react';
import {
  ReplacementSheet,
  SideSheet,
  Agent,
  FindReplace,
  RemoveShoppingCart,
} from '@desquared/wave-components-library';
import { ReplacementOptionStore, ReplacementSheetStore } from '@/store/sideSheet';
import { useQuery } from '@tanstack/react-query';
import { getV3Configuration } from '@/api/configuration';
import { useRouter } from 'next/router';
import { useTranslations } from 'next-intl';

export default function ReplacementSheetAside() {
  const [sideSheet, setSideSheet] = useState(ReplacementSheetStore.getValue());
  const [replacementOption, setReplacementOption] = useState(ReplacementOptionStore.getValue());
  const t = useTranslations('common.asides.replacement_options');
  const replacementOptionDisplayConfig = useMemo(
    () => ({
      phone: { subtitle: t('phone.subtitle'), icon: Agent },
      replace: { subtitle: t('replace.subtitle'), icon: FindReplace },
      none: {
        subtitle: t('none.subtitle'),
        icon: RemoveShoppingCart,
      },
    }),
    [t]
  );

  const [options, setOptions] = useState([
    {
      title: t('phone.default_title'),
      subtitle: replacementOptionDisplayConfig.phone.subtitle,
      icon: replacementOptionDisplayConfig.phone.icon,
      selected: replacementOption.option === 'phone',
      option: 'phone',
    },
    {
      title: t('replace.default_title'),
      subtitle: replacementOptionDisplayConfig.replace.subtitle,
      icon: replacementOptionDisplayConfig.replace.icon,
      selected: replacementOption.option === 'replace',
      option: 'replace',
    },
    {
      title: t('none.default_title'),
      subtitle: replacementOptionDisplayConfig.none.subtitle,
      icon: replacementOptionDisplayConfig.none.icon,
      selected: replacementOption.option === 'none',
      option: 'none',
    },
  ]);
  const { locale } = useRouter();

  const { data: productReplacementConfig } = useQuery({
    queryKey: ['config', locale],
    queryFn: ({ queryKey }) => getV3Configuration(queryKey[1]),
    select: data =>
      data.order.checkout.preferences.find(p => p.type === 'PRODUCT_REPLACEMENT_OPTIONS').productReplacementConfig,
  });

  useEffect(() => {
    if (!productReplacementConfig || productReplacementConfig?.options.length === 0) return;

    setOptions(
      productReplacementConfig.options
        .filter(option => option.enabled && option.visibleToUser)
        .map(option => ({
          title: option.title,
          subtitle: replacementOptionDisplayConfig[option.type].subtitle,
          icon: replacementOptionDisplayConfig[option.type].icon,
          selected: replacementOption.option === option.type,
          option: option.type,
          index: option.index,
        }))
        .sort((a, b) => a.index - b.index)
    );
  }, [productReplacementConfig, replacementOption, setOptions, replacementOptionDisplayConfig]);

  const setSelected = index => {
    const updatedOptions = [...options];
    updatedOptions.forEach((option, i) => {
      option.selected = i === index;
    });

    setOptions(updatedOptions);
  };
  const refOne = useRef(null);

  const onClick = () => {
    setTimeout(() => {
      ReplacementSheetStore.close();
    }, '300');
  };
  const onSubmit = () => {
    const item = options.find(obj => obj.selected === true);
    ReplacementOptionStore.update({ option: item.option, title: item.title });
    onClick();
  };
  useEffect(() => {
    const ReplacementSheetStore$ = ReplacementSheetStore.subscribe(setSideSheet);
    const ReplacementOptionStore$ = ReplacementOptionStore.subscribe(setReplacementOption);
    document.addEventListener('click', handleClickOutside, true);

    return () => {
      ReplacementSheetStore$.unsubscribe();
      ReplacementOptionStore$.unsubscribe();
      document.removeEventListener('click', handleClickOutside, false);
    };
  }, [sideSheet]);

  const handleClickOutside = e => {
    if (refOne.current && !refOne.current.contains(e.target)) {
      ReplacementSheetStore.close();
    }
  };

  useEffect(() => {
    ReplacementOptionStore.update({ option: 'phone', title: t('phone.default_title') });
  }, [t]);

  if (!sideSheet) return null;

  // useDisableBodyScroll();
  return (
    <div ref={refOne}>
      <SideSheet header={t('header')} hasClose={true} onClose={onClick} buttonText={t('save_btn')} onSubmit={onSubmit}>
        <ReplacementSheet subtitle={t('subtitle')} replacementOptions={options} setSelected={setSelected} />
      </SideSheet>
    </div>
  );
}
